import { CalendarDate, DateRange } from "@tournated-com/ui";

import { CapabilitySet } from "@/types/capability.types";
import { BaseClub, ClubReference } from "@/types/club.types";
import { AvatarFields, CountryCode, CurrencyCode, Id } from "@/types/common.types";
import { Organization } from "@/types/organization.types";
import { BasePlatform, PlatformReference } from "@/types/platform.types";
import { ScopePair } from "@/types/scope.types";
import { Sport, SportRef } from "@/types/sport.types";

export type User = {
  id: string;
  name: string;
  email: string;
  surname: string;
  avatar: string | null;
  role: UserRole | null;
  phone: string | null;
  isPhoneVerified: boolean;
  instagram: string | null;
  nation: CountryCode | null;
  gender: Gender | null;
  city: string | null;
  country: CountryCode | null;
  postalCode: string | null;
  emailConfirmed: boolean;
  birthDate: CalendarDate | null;
  uciId: string | null;
  wprPlayerId: string | null;
  wprUserId: string | null;
  additionalRoles: AdditionalUserRole[];
  userRatings?: UserRatingRow[] | null;
};

export type UserRatingRow = {
  provider: string;
  wtnRating?: number | null;
  duprSinglesRating?: number | null;
  duprDoublesRating?: number | null;
  wprCompetitionRating?: string | null;
  wprSocialRating?: string | null;
};

export type DetailedUser = Omit<User, "avatar"> &
  AvatarFields & {
    middleName: string | null;
    city: string | null;
    street: string | null;
    clubText: string | null;
    country: string | null;
    citizenship: CountryCode | null;
    ipinId: string | null;
    duprId: string | null;
    teId: string | null;
    ltsU10Id: string | null;
    createdAt: Date;
    atpId: string | null;
    wtaId: string | null;
    fideId: string | null;
    teU12Id: string | null;
    bonusU14: string | null;
    bonusU12: string | null;
    bonusPoints: string | null;
    itfPoints: string | null;
    fipPoints: string | null;
    fivb: string | null;
    bonusU16: string | null;
    otherPoints: string | null;
    wprUserId: string | null;
    lastResetPassword: Date | null;
    lastPasswordResetBy: User | null;
    wprPlayerId: string | null;
    entries: number | null;
    paidAmount: number | null;
    signupSource: UserSignupSource | null;
    ctfId: string | null;
    uciId: string | null;
    legalAddress: string | null;
    legalCity: string | null;
    legalCountry: CountryCode | null;
    legalPostalCode: string | null;
    legalAddressSameAsPublic: boolean;
    education: UserEducation | null;
  };

export type OrganizerUser = User &
  AvatarFields & {
    middleName: string | null;
    nameArmenian: string | null;
    surnameArmenian: string | null;
    representingCountry: string | null;
    locationCountry: string | null;
    stripeAccountId: string | null;
    stripeConnectOnboardingComplete: boolean | null;
  };

export type UserEducation = {
  institution: BaseClub;
  period: DateRange<CalendarDate> | null;
};

export type UserDetailEducation = {
  institution: ClubReference;
  period: DateRange<CalendarDate> | null;
};

/**
 * REST-backed user detail (`GET /dashboard/v1/users/:id`, also the email-lookup
 * result). Unlike the legacy GraphQL `DetailedUser`, this carries only the fields
 * the REST endpoint actually returns — no hardcoded-null third-party IDs, points,
 * or audit fields. Consumed by the update-user page + forms.
 */
export type UserDetail = AvatarFields & {
  id: string;
  internalId: number;
  tournatedTag: string;
  name: string;
  middleName: string | null;
  surname: string;
  /** Unique login email — read-only on the update form; changing it is a separate concern. */
  email: string;
  /** Public contact email — separate from the login `email`, no uniqueness constraint. */
  contactEmail: string | null;
  phone: string | null;
  /** Public contact phone (E.164) — separate from the login-adjacent `phone`. */
  contactPhone: string | null;
  gender: Gender | null;
  birthDate: CalendarDate | null;
  /** Representing country (sport nationality). */
  nation: CountryCode | null;
  /** US state the player represents — only meaningful while `nation` is `US`. */
  representingState: USState | null;
  /** Address (residence) state — only meaningful while `country` is `US`. PII; dashboard detail only. */
  state: USState | null;
  personalId: string | null;
  additionalRoles: AdditionalUserRole[];
  /** Residence / public address country. */
  country: CountryCode | null;
  city: string | null;
  address: string | null;
  postalCode: string | null;
  legalAddressSameAsPublic: boolean;
  legalAddress: string | null;
  legalCity: string | null;
  legalCountry: CountryCode | null;
  legalPostalCode: string | null;
  education: UserDetailEducation | null;
};

export type UserSignupSource = "web" | "mobile" | "admin" | "manager";

export const UserRoleValues = [
  "federation",
  "clubAdministrator",
  "coach",
  "superAdmin",
  "admin",
  "athlete",
  "user",
  "fan",
  "member",
] as const;

export type UserRole = (typeof UserRoleValues)[number];

export const AdditionalUserRoleValues = [
  "athlete",
  "federationMember",
  "clubOwner",
  "clubManager",
  "referee",
  "tournamentOrganizer",
  "sportFan",
  "other",
] as const;

export type AdditionalUserRole = (typeof AdditionalUserRoleValues)[number];

export type CurrentUser = AvatarFields & {
  id: string;
  internalId: number;
  name: string;
  surname: string;
  email: string;
  role: UserRole | null;
  country: CountryCode | null;
  memberOfPlatformIds: string[];
  isLeagueManager: boolean;
  hasFinishedTour: boolean;
};

export type UserPlatformRole =
  | "platformManager"
  | "platformOwner"
  | "leagueManager"
  | "subOrganization"
  | "federation"
  | "superAdmin";

export type UserWithSports = {
  sports: {
    id: Id;
    club: BaseClub | null;
    team: string | null;
    sport: Sport;
  }[];
};

export type UserWithPlatforms = {
  platforms: BasePlatform[];
  signupPlatform: BasePlatform | null;
};

export type UserWithMemberOfPlatforms = {
  memberOfPlatforms: { id: string }[];
};

export type UserWithOrganizations = {
  organizations: Organization[];
};

export type UserWithClub = {
  club: BaseClub | null;
};
export const GenderValues = ["male", "female"] as const;
export type Gender = (typeof GenderValues)[number];

/** USPS abbreviations for the 50 states and Washington, DC. */
export const USStateValues = [
  "AL",
  "AK",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DE",
  "FL",
  "GA",
  "HI",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  "ME",
  "MD",
  "MA",
  "MI",
  "MN",
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "OH",
  "OK",
  "OR",
  "PA",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VT",
  "VA",
  "WA",
  "WV",
  "WI",
  "WY",
  "DC",
] as const;

export type USState = (typeof USStateValues)[number];

export const isUSState = (value: string): value is USState =>
  USStateValues.some((state) => state === value);
export const ThirdPartyLicenseTypeValues = [
  "dupr",
  "wpr",
  "ipin",
  "itfPoints",
  "fipPoints",
  "fideId",
  "fivb",
] as const;

export type ThirdPartyLicenseType = (typeof ThirdPartyLicenseTypeValues)[number];

export type ThirdPartyLicenseReference = {
  id: number;
  title: string;
  type: string | null;
};

export type UserReference = AvatarFields & {
  id: string;
  name: string;
  surname: string;
  email: string;
  /** Human-facing Tournated ID (6-char code); null when the user has no tag assigned, undefined when the source ref did not load it. */
  tournatedTag?: string | null;
};

/**
 * External player-connection id fields exposed on the User entity. Each value is
 * the exact User column name and also the key the dashboard PATCH body accepts.
 * The list is data-driven per platform (via each `ThirdPartyLicense.field`), so
 * a platform may surface any subset of these columns.
 */
export const ConnectionFieldValues = [
  "fivb",
  "duprId",
  "ipinId",
  "teId",
  "atpId",
  "wtaId",
  "fideId",
  "pdlId",
  "uciId",
  "ltsU10Id",
  "teU12Id",
  "wprPlayerId",
  "itf_points",
  "cpcPoints",
  "maxwillPoints",
  "fipPoints",
  "otherPoints",
] as const;

export type ConnectionField = (typeof ConnectionFieldValues)[number];

/** Per-user connection id values, keyed by the User column name. */
export type UserConnections = { [key in ConnectionField]: string | null };

/**
 * One player-connection column resolved for the current filter scope. Built from
 * `CONNECTION_FIELD_REGISTRY` by `useUserConnectionColumns` and consumed by the
 * users table. Labels come from `useGetConnectionFieldLabel(field)`.
 */
export type ConnectionColumn = {
  field: ConnectionField;
  columnId: string;
  editable: boolean;
};

export type UserListItem = AvatarFields & {
  id: string;
  /**
   * Temporary numeric DB id, exposed only to bridge redirects to legacy pages
   * that still take the numeric id in the URL.
   */
  internalId: number;
  name: string;
  surname: string;
  email: string;
  phone: string | null;
  nation: CountryCode | null;
  country: CountryCode | null;
  city: string | null;
  birthDate: CalendarDate | null;
  gender: Gender | null;
  role: UserRole | null;
  createdAt: Date;
  /**
   * When the user joined the single `(platform, organization)` pair the list
   * is filtered to. Only populated by the backend when the query carries
   * exactly one federation id; null otherwise (fall back to `createdAt`).
   */
  joinedAt: Date | null;
  entries: number | null;
  transactionsTotal: number | null;
  transactionsCurrency: CurrencyCode | null;
  signupSource: UserSignupSource | null;
  signupPlatform: PlatformReference | null;
  /**
   * Caller-visible `(platform, organization)` membership pairs — see
   * `ScopePair`. The Platform / Organization columns derive their distinct
   * ref lists from these pairs.
   */
  scopes: ScopePair[];
  sports: SportRef[];
  /**
   * Every club the user belongs to across their active sports (deduplicated,
   * ordered by sport title then club name).
   */
  clubs: ClubReference[];
  thirdPartyLicenses: ThirdPartyLicenseType[];
  tournatedTag: string | null;
  ctfId: string | null;
  /**
   * External player-connection id values (fivb, duprId, …), read directly from
   * the REST list row.
   */
  connections: UserConnections | null;
  /**
   * Per-row `USER:*` capability keys the caller holds on this row's scope
   * node, resolved by the backend list endpoint. Row actions gate on these
   * per-row keys (strict RBAC) — see docs/capabilities.md.
   */
  capabilities: CapabilitySet;
};

export const UsersTableColumnValues = [
  "number",
  "ctfId",
  "user",
  "contacts",
  "age",
  "roles",
  "sports",
  "clubs",
  "entries",
  "transactions",
  "thirdParties",
  "platforms",
  "organizations",
  "joined",
] as const;

export type UsersTableColumn = (typeof UsersTableColumnValues)[number];

export const UserExportColumnValues = [
  "name",
  "surname",
  "email",
  "phone",
  "birthDate",
  "nation",
  "role",
  "gender",
  "city",
  "country",
  "club",
] as const;

/** Fixed (always-offered) users-export column keys. */
export type UserExportBaseColumn = (typeof UserExportColumnValues)[number];

/**
 * Any users-export column key: a fixed column, or a player-connection column
 * (key = the `ConnectionField` name, which is also the backend export key).
 * Connection columns are offered per filter scope — see `useUserConnectionColumns`.
 */
export type UserExportColumn = UserExportBaseColumn | ConnectionField;
export type ThirdPartyLicense = {
  id: number;
  title: string | null;
  organizationName: string | null;
  organizationAbbreviation: string | null;
  type: string | null;
  sports: Sport[];
};
